import React, { setState, useState, useEffect, useLayoutEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import moment from "moment";
import styles from "./OrderSheet.module.css";
import { makeStyles, useTheme, alpha } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import { useLocation } from "react-router-dom";
import { getProfilesByUser } from "../../actions/profile";
import { getRet, createRet, updateRet } from '../../actions/returnActions'
import Spinner from "../Spinner/Spinner";
import { useSnackbar } from "react-simple-snackbar";
import DateFnsUtils from "@date-io/date-fns";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";
import { ReactGrid } from "@silevis/reactgrid";
import "@silevis/reactgrid/styles.css";
import axios from "axios";
import { saveAs } from "file-saver";
import ProgressButton from "react-progress-button";
import { useMediaQuery } from "@material-ui/core";
import { fetchOrderSheetData } from "../../api";

const useStyles1 = makeStyles((theme) => ({
  root: {
    flexShrink: 0,
    marginLeft: theme.spacing(2.5),
  },
}));

const FittedOrderGrid = ({ columns, rows, ...props }) => {
  const containerRef = useRef(null);
  const [availableWidth, setAvailableWidth] = useState(null);
  // ReactGrid enforces a 40px minimum even when a smaller column is requested.
  const naturalWidth = columns.reduce((total, column) => total + Math.max(40, column.width), 0);

  useLayoutEffect(() => {
    const container = containerRef.current;
    let frame;
    const measure = () => setAvailableWidth(Math.max(0, container.clientWidth - 2));
    measure();
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    });
    observer.observe(container);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, []);

  const width = Math.min(naturalWidth, availableWidth ?? naturalWidth);
  const minimumWidth = columns.length * 40;
  const columnScale = Math.max(0, (width - minimumWidth) / (naturalWidth - minimumWidth));
  const scale = width / naturalWidth;
  const fittedColumns = columns.map((column) => ({
    ...column,
    width: Math.floor(40 + (Math.max(40, column.width) - 40) * columnScale),
  }));

  return (
    <div
      ref={containerRef}
      className={styles.gridContainer}
      style={{ maxWidth: naturalWidth + 2, fontSize: `${16 * scale}px` }}
    >
      <ReactGrid
        {...props}
        columns={fittedColumns}
        rows={rows.map((row) => ({ ...row, height: Math.max(22, Math.floor((row.height || 25) * scale)) }))}
      />
    </div>
  );
};

const OrderSheet = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const [orderSheetInvoices, setOrderSheetInvoices] = useState([]);
  const [orderSheetWhites, setOrderSheetWhites] = useState([]);
  const [orderSheetLoading, setOrderSheetLoading] = useState(false);
  const invoices = orderSheetInvoices;
  const whites = orderSheetWhites;
  const invoicesLoading = orderSheetLoading;
  const whitesLoading = orderSheetLoading;
  let {ret} = useSelector(state => {
    return state.returns});
  const user = JSON.parse(localStorage.getItem("profile"));
  const isLoading = useSelector((state) => state.routes.isLoading);
  // eslint-disable-next-line
  const [openSnackbar, closeSnackbar] = useSnackbar();
  const [sendStatus, setSendStatus] = useState(null);
  const [sendLawStatus, setSendLawStatus] = useState(null);
  const [returnItems, setReturnItems] = useState(ret ? ret.items : []);
  const [selectedDate, setSelectedDate] = useState(() => {
    return moment().format("MM/DD/yyyy");
  });
  const [company, setCompany] = useState({});
  const [filteredInvoices, setFilteredInvoices] = useState(() => {
    return invoices.filter(
      (i) => moment(i.receivedByDate).format("MM/DD/yyyy") === selectedDate
    );
  });
  const [filteredWhites, setFilteredWhites] = useState(() => {
    return whites.filter(
      (i) => moment(i.receivedByDate).format("MM/DD/yyyy") === selectedDate
    );
  });
  const [items, setItems] = useState([]);
  const [whiteItems, setWhiteItems] = useState([]);
  const [dnoItems, setDNOItems] = useState([]);
  const [sendHistory, setSendHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const { profiles } = useSelector((state) => state.profiles);
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("md"));
  useEffect(() => {
    dispatch(
      getProfilesByUser({ search: user?.result?._id || user?.result.googleId })
    );
    let dt = Date.parse(selectedDate)
    dispatch(getRet(dt))
    // eslint-disable-next-line
  }, [location, dispatch]);

  useEffect(() => {
    let cancelled = false;
    setOrderSheetLoading(true);
    fetchOrderSheetData({ date: selectedDate, includeWhites: true })
      .then(({ data }) => {
        if (cancelled) return;
        setOrderSheetInvoices(data?.data?.invoices || []);
        setOrderSheetWhites(data?.data?.whites || []);
      })
      .catch((error) => {
        console.log(error?.response || error);
        if (!cancelled) {
          setOrderSheetInvoices([]);
          setOrderSheetWhites([]);
        }
      })
      .finally(() => {
        if (!cancelled) setOrderSheetLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedDate]);

  useEffect(() => {
    setFilteredInvoices(() => {
      let filtered = invoices.filter((i) => {
        return moment(i.receivedByDate).format("MM/DD/yyyy") === selectedDate;
      });
      return filtered;
    });

    setFilteredWhites(() => {
      let filtered = whites.filter((i) => {
        return moment(i.receivedByDate).format("MM/DD/yyyy") === selectedDate;
      });
      return filtered;
    });
    if (ret) {
      setReturnItems(ret.items)
    } else {
      setReturnItems([])
    }
  }, [selectedDate, invoices, whites, ret]);

  useEffect(() => {
    setItems(generateOrderSheet());
  }, [filteredInvoices]);

  useEffect(() => {
    setWhiteItems(generateWhiteOrderSheet());
  }, [filteredWhites]);

  useEffect(() => {
    setDNOItems(generateDNOSheet());
  }, [filteredInvoices]);

  const fetchSendHistory = async (dateValue) => {
    if (!dateValue) return;
    setHistoryLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API}/pdfs/order-sheet-history`, {
        params: { selectedDate: dateValue },
      });
      setSendHistory(Array.isArray(response?.data?.data) ? response.data.data : []);
    } catch (error) {
      console.log(error);
      setSendHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchSendHistory(selectedDate);
    // eslint-disable-next-line
  }, [selectedDate]);
  


  const generateOrderSheet = () => {
    let productsMap = {};
    filteredInvoices.forEach((invoice) => {
      invoice.items.forEach((i) => {
        if (!i.doNotOrder) {
          if ((!productsMap.hasOwnProperty(i.modelNumber) && !i.lockup) || (!productsMap.hasOwnProperty(`#${i.modelNumber}`) && i.lockup)) {
            let pCost = dollar_formatter.format(i.unitCost);
            let pPrice = dollar_formatter.format(i.unitPrice);
            let pLockup = i.lockup ? `#${i.modelNumber}` : i.modelNumber
            productsMap[pLockup] = {
              quantity: i.quantity,
              cost: pCost,
              modelNumber: pLockup,
              price: pPrice
            };
          } else {
            let pLockup = i.lockup ? `#${i.modelNumber}` : i.modelNumber
            let quantity = parseInt(productsMap[pLockup]["quantity"]);
            productsMap[pLockup]["quantity"] = (
              quantity + parseInt(i.quantity)
            ).toString();
          }
        }
      });
    });
    return Object.keys(productsMap).map((key) => {
      return productsMap[key];
    });
  };

  const generateWhiteOrderSheet = () => {
    let productsMap = {};
    filteredWhites.forEach((invoice) => {
      invoice.items.forEach((i) => {
        if (!i.doNotOrder) {
          if ((!productsMap.hasOwnProperty(i.modelNumber) && !i.lockup) || (!productsMap.hasOwnProperty(`#${i.modelNumber}`) && i.lockup)) {
            let pCost = dollar_formatter.format(i.unitCost);
            let pPrice = dollar_formatter.format(i.unitPrice);
            let pLockup = i.lockup ? `#${i.modelNumber}` : i.modelNumber
            productsMap[pLockup] = {
              quantity: i.quantity,
              cost: pCost,
              modelNumber: pLockup,
              price: pPrice
            };
          } else {
            let pLockup = i.lockup ? `#${i.modelNumber}` : i.modelNumber
            let quantity = parseInt(productsMap[pLockup]["quantity"]);
            productsMap[pLockup]["quantity"] = (
              quantity + parseInt(i.quantity)
            ).toString();
          }
        }
      });
    });
    return Object.keys(productsMap).map((key) => {
      return productsMap[key];
    });
  };

  const generateDNOSheet = () => {
    let productsMap = {};
    filteredInvoices.forEach((invoice) => {
      invoice.items.forEach((i) => {
        if (i.doNotOrder) {
          const parsedUnitCost = Number(`${i.unitCost ?? ''}`.replace(/[^\d.-]/g, ''));
          const unitCost = Number.isFinite(parsedUnitCost) ? parsedUnitCost : 0;
          const productKey = `${i.modelNumber}::${unitCost.toFixed(2)}`;
          if (!productsMap.hasOwnProperty(productKey)) {
            let pCost = dollar_formatter.format(unitCost);
            let pPrice = dollar_formatter.format(i.unitPrice);
            productsMap[productKey] = {
              quantity: i.quantity,
              modelNumber: i.modelNumber,
              cost: pCost,
              price: pPrice
            };
          } else {
            let quantity = parseInt(productsMap[productKey]["quantity"]);
            productsMap[productKey]["quantity"] = (
              quantity + parseInt(i.quantity)
            ).toString();
          }
        }
      });
    });
    return Object.keys(productsMap).map((key) => {
      return productsMap[key];
    });
  };

  const sendOrderSheet = (e, emailList, flag) => {
    e.preventDefault();
    if (!emailList || `${emailList}`.trim() === "") {
      openSnackbar("Email list is empty. Please update it in Settings.");
      flag === 'L' ? setSendLawStatus("error") : setSendStatus("error");
      return;
    }
    flag === 'L' ? setSendLawStatus("loading") : setSendStatus("loading");
    const userResult = user?.result || {};
    const sentByName =
      userResult.name ||
      `${userResult.firstName || ''} ${userResult.lastName || ''}`.trim() ||
      userResult.email ||
      '';
    const sentByEmail = userResult.email || '';
    const sentByUserId = userResult._id || userResult.googleId || '';
    axios
      .post(`${process.env.REACT_APP_API}/pdfs/send-order-sheet`, {
        sentDate: moment().format("MM/DD/yyyy"),
        selectedDate: selectedDate,
        returnRows: returnItems,
        rows: items,
        email: emailList,
        flag: flag,
        sentByName,
        sentByEmail,
        sentByUserId,
      })
      .then(() => {
        flag === 'L' ? setSendLawStatus("success") : setSendStatus("success");
        fetchSendHistory(selectedDate);
      })
      .catch((error) => {
        console.log(error);
        openSnackbar("Unable to send order sheet. Please try again.");
        flag === 'L' ? setSendLawStatus("error") : setSendStatus("error");
      });
  };

  // Create our number formatter.
  const dollar_formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });

  const formatDollarsToCents = function (value) {
    value = `${value}`.replace(/[^\d.-]/g, "");
    if (value && value.includes(".")) {
      value = value.substring(0, value.indexOf(".") + 3);
    }

    return value ? Math.round(parseFloat(value) * 100) : 0;
  };

  const formatCentsToDollars = function (value) {
    value = `${value}`.replace(/[^\d.-]/g, "");
    value = parseFloat(value);
    return value ? value / 100 : 0;
  };

  const getPretaxRevenueCents = (invoice = {}) => {
    const totalCents = formatDollarsToCents(invoice.total || 0);
    const salesTaxCents = formatDollarsToCents(invoice.salesTax || 0);
    if (totalCents > 0) return Math.max(totalCents - salesTaxCents, 0);
    return formatDollarsToCents(invoice.subTotal || 0) + formatDollarsToCents(invoice.deliveryFee || 0);
  };

  const priceDifferenceGroups = React.useMemo(() => {
    const modelMap = new Map();
    const moneyToCents = (value) => {
      let normalized = `${value}`.replace(/[^\d.-]/g, "");
      if (normalized && normalized.includes(".")) {
        normalized = normalized.substring(0, normalized.indexOf(".") + 3);
      }
      return normalized ? Math.round(parseFloat(normalized) * 100) : 0;
    };

    filteredInvoices.forEach((invoice) => {
      const invoiceItems = Array.isArray(invoice?.items) ? invoice.items : [];
      invoiceItems.forEach((item) => {
        if (!item?.modelNumber) return;

        const modelNumber = String(item.modelNumber).replace(/^#/, "").trim();
        if (!modelNumber) return;

        const unitPriceCents = moneyToCents(item.unitPrice);
        const unitCostCents = moneyToCents(item.unitCost);
        const quantity = Number(item.quantity) || 0;
        const modelEntry = modelMap.get(modelNumber) || {
          modelNumber,
          priceGroups: new Map(),
        };
        const priceKey = `${unitPriceCents}:${unitCostCents}`;
        const priceEntry = modelEntry.priceGroups.get(priceKey) || {
          unitPriceCents,
          unitCostCents,
          quantity: 0,
          sourceTypes: new Set(),
          invoices: new Map(),
        };
        const invoiceId = invoice?._id || invoice?.id || invoice?.invoiceNumber;

        priceEntry.quantity += quantity;
        if (item.doNotOrder) priceEntry.sourceTypes.add("Inventory");
        if (item.lockup) priceEntry.sourceTypes.add("Lockup");
        if (invoiceId) {
          priceEntry.invoices.set(invoiceId, {
            id: invoiceId,
            invoiceNumber: invoice?.invoiceNumber || invoiceId,
            customer: invoice?.client?.store || invoice?.client?.name || "",
            receivedByDate: invoice?.receivedByDate,
          });
        }

        modelEntry.priceGroups.set(priceKey, priceEntry);
        modelMap.set(modelNumber, modelEntry);
      });
    });

    return Array.from(modelMap.values())
      .map((modelEntry) => ({
        ...modelEntry,
        priceGroups: Array.from(modelEntry.priceGroups.values())
          .sort((a, b) => a.unitCostCents - b.unitCostCents || a.unitPriceCents - b.unitPriceCents)
          .map((priceEntry) => ({
            ...priceEntry,
            sourceTypes: Array.from(priceEntry.sourceTypes).sort(),
            invoices: Array.from(priceEntry.invoices.values()).sort((a, b) => {
              const customerCompare = String(a.customer || "").localeCompare(String(b.customer || ""));
              if (customerCompare) return customerCompare;
              return String(a.receivedByDate || "").localeCompare(String(b.receivedByDate || ""));
            }),
          })),
      }))
      .filter((modelEntry) => {
        const costs = new Set(modelEntry.priceGroups.map((priceGroup) => priceGroup.unitCostCents));
        return costs.size > 1;
      })
      .sort((a, b) => a.modelNumber.localeCompare(b.modelNumber));
  }, [filteredInvoices]);

  const handleDateChange = (value) => {
    if (value !== null) {
      let val = moment(value).format("MM/DD/yyyy");
      let dt = Date.parse(val)
      dispatch(getRet(dt))
      setSelectedDate(val);
    }
  };

  if (!user) {
    history.push("/login");
  }

  const dnoHeaderRow = {
    rowId: 'dnoHeader',
    cells: [
      {
        type: "header",
        text: "Model #",
      },
      {
        type: "header",
        text: "Qty",
      },
      {
        type: "header",
        text: "Cost",
      },
    ]
  }

  const headerRow = {
    rowId: "header",
    cells: [
      {
        type: "header",
        text: "Model #",
      },
      {
        type: "header",
        text: "Qty",
      },
      {
        type: "header",
        text: "Cost",
      },
    ],
  };

  const headerReturnRow = {
    rowId: "returnsHeader",
    cells: [
      {
        type: "header",
        text: "Model #",
      },
      {
        type: "header",
        text: "Qty",
      },
      {
        type: "header",
        text: "Return",
      },
      {
        type: "header",
        text: "Ticket #",
      },
    ],
  }

  const mobileHeaderRow = {
    rowId: "header",
    cells: [
      {
        type: "header",
        text: "Model #",
      },
      {
        type: "header",
        text: "Qty",
      },
      {
        type: "header",
        text: "Cost",
      },
    ],
  };

  let totalCostCents = 0;
  let totalCostInventoryCents = 0;
  let totalCostInventory = 0;
  let totalCents = 0;
  let totalCost = "";
  let profit = "";
  let revenue = "";
  let bought = "";
  let deliveryCostTotal = "";
  let deliveryCostCents = 0;

  let totalCostCentsWhites = 0;
  let totalCostInventoryCentsWhites = 0;
  let totalCostInventoryWhites = 0;
  let totalCentsWhites = 0;
  let totalCostWhites = "";
  let profitWhites = "";
  let revenueWhites = "";
  let boughtWhites = "";
  let deliveryCostTotalWhites = "";
  let deliveryCostCentsWhites = 0;
  
  const calculateTotal = () => {
    totalCostCents = 0;
    totalCostInventoryCents = 0;
    totalCents = 0;
    deliveryCostCents = 0;
    for (var i = 0; i < filteredInvoices.length; i += 1) {
      let invoice = filteredInvoices[i];
      totalCents += getPretaxRevenueCents(invoice);
      deliveryCostCents += formatDollarsToCents(invoice.deliveryCost || 0);
      for (var j = 0; j < invoice.items.length; j ++) {
        let item = invoice.items[j];
        if (!item.doNotOrder) {
          let qty = parseInt(item.quantity)
          let costCents = formatDollarsToCents(item.unitCost)
          totalCostCents += qty * costCents;
        } else {
          let qty = parseInt(item.quantity)
          let costCents = formatDollarsToCents(item.unitCost)
          totalCostInventoryCents += qty * costCents;
        }
      }
    }
    let profitCents = totalCents - (totalCostCents + totalCostInventoryCents + deliveryCostCents);
    profit = dollar_formatter.format(formatCentsToDollars(profitCents));
    revenue = dollar_formatter.format(formatCentsToDollars(totalCents));
    totalCost = dollar_formatter.format(formatCentsToDollars(totalCostCents + totalCostInventoryCents + deliveryCostCents));
    totalCostInventory = dollar_formatter.format(formatCentsToDollars(totalCostInventoryCents));
    bought = dollar_formatter.format(formatCentsToDollars(totalCostCents));
    deliveryCostTotal = dollar_formatter.format(formatCentsToDollars(deliveryCostCents));
  };

  const calculateTotalWhites = () => {
    totalCostCentsWhites = 0;
    totalCostInventoryCentsWhites = 0;
    totalCentsWhites = 0;
    deliveryCostCentsWhites = 0;
    for (var i = 0; i < filteredWhites.length; i += 1) {
      let invoice = filteredWhites[i];
      totalCentsWhites += getPretaxRevenueCents(invoice);
      deliveryCostCentsWhites += formatDollarsToCents(invoice.deliveryCost || 0);
      for (var j = 0; j < invoice.items.length; j ++) {
        let item = invoice.items[j];
        if (!item.doNotOrder) {
          let qty = parseInt(item.quantity)
          let costCents = formatDollarsToCents(item.unitCost)
          totalCostCentsWhites += qty * costCents;
        } else {
          let qty = parseInt(item.quantity)
          let costCents = formatDollarsToCents(item.unitCost)
          totalCostInventoryCentsWhites += qty * costCents;
        }
      }
    }
    let profitCents = totalCentsWhites - (totalCostCentsWhites + totalCostInventoryCentsWhites + deliveryCostCentsWhites);
    profitWhites = dollar_formatter.format(formatCentsToDollars(profitCents));
    revenueWhites = dollar_formatter.format(formatCentsToDollars(totalCentsWhites));
    totalCostWhites = dollar_formatter.format(formatCentsToDollars(totalCostCentsWhites + totalCostInventoryCentsWhites + deliveryCostCentsWhites));
    totalCostInventoryWhites = dollar_formatter.format(formatCentsToDollars(totalCostInventoryCentsWhites));
    boughtWhites = dollar_formatter.format(formatCentsToDollars(totalCostCentsWhites));
    deliveryCostTotalWhites = dollar_formatter.format(formatCentsToDollars(deliveryCostCentsWhites));
  };

  const getRows = () => {
    return [
      headerRow,
      ...items.map((p, index) => ({
        rowId: index,
        cells: [
          { type: "text", text: p.modelNumber },
          { type: "text", text: p.quantity },
          { type: "text", text: p.cost },
        ],
      })),
      {
        rowId: "totalTitle",
        cells: [
          { type: "text", text: "" },
          { type: "text", text: "" },
          { type: "text", text: `Total` },
        ],
      },
      {
        rowId: "totalBought",
        cells: [
          { type: "text", text: "" },
          { type: "text", text: "" },
          { type: "text", text: `Bought: ${bought}` },
        ],
      },
      {
        rowId: "totalInventory",
        cells: [
          { type: "text", text: "" },
          { type: "text", text: "" },
          { type: "text", text: `Inventory: ${totalCostInventory}` },
        ],
      },
      {
        rowId: "totalDeliveryCost",
        cells: [
          { type: "text", text: "" },
          { type: "text", text: "" },
          { type: "text", text: `Delivery Cost: ${deliveryCostTotal}` },
        ],
      },
      {
        rowId: "total",
        cells: [
          { type: "text", text: "" },
          { type: "text", text: "" },
          { type: "text", text: `Cost: ${totalCost}` },
        ],
      },
      {
        rowId: "revenue",
        cells: [
          { type: "text", text: "" },
          { type: "text", text: "" },
          { type: "text", text: `Revenue: ${revenue}` },
        ],
      },
      {
        rowId: "Profit",
        cells: [
          { type: "text", text: "" },
          { type: "text", text: "" },
          { type: "text", text: `Profit: ${profit}` },
        ],
      },
    ];
  };

  const getWhiteRows = () => {
    return [
      headerRow,
      ...whiteItems.map((p, index) => ({
        rowId: index,
        cells: [
          { type: "text", text: p.modelNumber },
          { type: "text", text: p.quantity },
          { type: "text", text: p.cost },
        ],
      })),
      {
        rowId: "totalTitle",
        cells: [
          { type: "text", text: "" },
          { type: "text", text: "" },
          { type: "text", text: `Total` },
        ],
      },
      {
        rowId: "totalBought",
        cells: [
          { type: "text", text: "" },
          { type: "text", text: "" },
          { type: "text", text: `Bought: ${boughtWhites}` },
        ],
      },
      {
        rowId: "totalInventory",
        cells: [
          { type: "text", text: "" },
          { type: "text", text: "" },
          { type: "text", text: `Inventory: ${totalCostInventoryWhites}` },
        ],
      },
      {
        rowId: "totalDeliveryCost",
        cells: [
          { type: "text", text: "" },
          { type: "text", text: "" },
          { type: "text", text: `Delivery Cost: ${deliveryCostTotalWhites}` },
        ],
      },
      {
        rowId: "total",
        cells: [
          { type: "text", text: "" },
          { type: "text", text: "" },
          { type: "text", text: `Cost: ${totalCostWhites}` },
        ],
      },
      {
        rowId: "revenue",
        cells: [
          { type: "text", text: "" },
          { type: "text", text: "" },
          { type: "text", text: `Revenue: ${revenueWhites}` },
        ],
      },
      {
        rowId: "Profit",
        cells: [
          { type: "text", text: "" },
          { type: "text", text: "" },
          { type: "text", text: `Profit: ${profitWhites}` },
        ],
      },
    ];
  };

  const getReturnRows = () => {
      return [
        headerReturnRow,
        ...returnItems.map((p, index) => ({
          rowId: index,
          cells: [
            { type: "text", text: p.modelNumber },
            { type: "text", text: p.quantity.toString() },
            { type: "dropdown", isOpen: p.isOpen, values: [{label: "", value: ""}, {label: "Exchange", value: "Exchange"}, {label: "Cancel", value: "Cancel"}, {label: "ASAP", value: "ASAP"}], selectedValue: p.returnType},
            { type: "text", text: p.ticketNumber.toString()}
          ],
        })),
      ]
    // let dt = Date.parse(selectedDate)
    // let response = await axios.get(
    //   `${process.env.REACT_APP_API}/rets?returnDate=${dt}`
    // ).then(response => {
      
    // })
    // return response;
  };

  const getDNORows = () => {
    return [
      dnoHeaderRow,
      ...dnoItems.map((p, index) => ({
        rowId: index,
        cells: [
          { type: "text", text: p.modelNumber },
          { type: "text", text: p.quantity },
          { type: "text", text: p.cost, nonEditable: true },
        ],
      })),
    ];
  }
  const getMobileRows = () => {
    return [
      mobileHeaderRow,
      ...items.map((p, index) => ({
        rowId: index,
        cells: [
          { type: "text", text: p.modelNumber },
          { type: "text", text: p.quantity },
          { type: "text", text: p.cost },
        ],
      })),
      {
        rowId: "totalTitle",
        cells: [
          { type: "text", text: "" },
          { type: "text", text: "" },
          { type: "text", text: `Total` },
        ],
      },
      {
        rowId: "totalBought",
        cells: [
          { type: "text", text: "" },
          { type: "text", text: "" },
          { type: "text", text: `Bought: ${bought}` },
        ],
      },
      {
        rowId: "totalInventory",
        cells: [
          { type: "text", text: "" },
          { type: "text", text: "" },
          { type: "text", text: `Inventory: ${totalCostInventory}` },
        ],
      },
      {
        rowId: "totalDeliveryCost",
        cells: [
          { type: "text", text: "" },
          { type: "text", text: "" },
          { type: "text", text: `Delivery Cost: ${deliveryCostTotal}` },
        ],
      },
      {
        rowId: "total",
        cells: [
          { type: "text", text: "" },
          { type: "text", text: "" },
          { type: "text", text: `Cost: ${totalCost}` },
        ],
      },
      {
        rowId: "revenue",
        cells: [
          { type: "text", text: "" },
          { type: "text", text: "" },
          { type: "text", text: `Revenue: ${revenue}` },
        ],
      },
      {
        rowId: "Profit",
        cells: [
          { type: "text", text: "" },
          { type: "text", text: "" },
          { type: "text", text: `Profit: ${profit}` },
        ],
      },
    ];
  };

  const getWhiteMobileRows = () => {
    return [
      mobileHeaderRow,
      ...whiteItems.map((p, index) => ({
        rowId: index,
        cells: [
          { type: "text", text: p.modelNumber },
          { type: "text", text: p.quantity },
          { type: "text", text: p.cost },
        ],
      })),
      {
        rowId: "totalTitle",
        cells: [
          { type: "text", text: "" },
          { type: "text", text: "" },
          { type: "text", text: `Total` },
        ],
      },
      {
        rowId: "totalBought",
        cells: [
          { type: "text", text: "" },
          { type: "text", text: "" },
          { type: "text", text: `Bought: ${boughtWhites}` },
        ],
      },
      {
        rowId: "totalInventory",
        cells: [
          { type: "text", text: "" },
          { type: "text", text: "" },
          { type: "text", text: `Inventory: ${totalCostInventoryWhites}` },
        ],
      },
      {
        rowId: "total",
        cells: [
          { type: "text", text: "" },
          { type: "text", text: "" },
          { type: "text", text: `Cost: ${totalCostWhites}` },
        ],
      },
      {
        rowId: "revenue",
        cells: [
          { type: "text", text: "" },
          { type: "text", text: "" },
          { type: "text", text: `Revenue: ${revenueWhites}` },
        ],
      },
      {
        rowId: "Profit",
        cells: [
          { type: "text", text: "" },
          { type: "text", text: "" },
          { type: "text", text: `Profit: ${profitWhites}` },
        ],
      },
    ];
  };

  calculateTotal();
  calculateTotalWhites();

  const rows = getRows(items);
  const whiteRows = getWhiteRows(whiteItems);
  const dnoRows = getDNORows(dnoItems);
  const mobileRows = getMobileRows(items);
  const whieMobileRows = getWhiteMobileRows(whiteItems);
  const returnRows = getReturnRows()
  const cols = [
    { columnId: "modelNumber", width: 250 },
    { columnId: "quantity", width: 250 },
    { columnId: "cost", width: 250 },
  ];

  const returnCols = [
    { columnId: "modelNumber", width: 250 },
    { columnId: "quantity", width:250 },
    { columnId: "returnType", width: 250 },
    { columnId: "ticketNumber", width: 250}
  ];

  const dnoCols = [
    { columnId: "modelNumber", width: 250 },
    { columnId: "quantity", width: 100 },
    { columnId: "cost", width: 150 },
  ]

  const returnMobileCols = [
    { columnId: "modelNumber", width: 150 },
    { columnId: "quantity", width: 40 },
    { columnId: "returnType", width: 150 },
    { columnId: "ticketNumber", width: 60}
  ];

  const mobileCols = [
    { columnId: "modelNumber", width: 135 },
    { columnId: "quantity", width: 40 },
    { columnId: "cost", width: 200 },
  ];

  const applyChanges = (changes, prev) => {
    changes.forEach((change) => {
      console.log(change);
      const rowIndex = change.rowId;
      const fieldName = change.columnId;
      if (change.type === 'dropdown') {
        if (change.newCell.selectedValue !== change.previousCell.selectedValue) {
          prev[rowIndex][fieldName] = change.newCell.selectedValue;
        }
        prev[rowIndex]['isOpen'] = change.newCell.isOpen;
      } else {
        prev[rowIndex][fieldName] = change.newCell.text;
      }
      
    });
    return [...prev];
  };

  const handleChanges = (changes) => {
    setItems((prev) => applyChanges(changes, prev));
  };

  const handleReturnChanges = (changes) => {
    setReturnItems((prev) => applyChanges(changes, prev));
  }

  const handleDNOChanges = (changes) => {
    setDNOItems((prev) => applyChanges(changes, prev));
  }

  const handleAddDNORowClick = () => {
    setDNOItems((prev) => {
      return [
        ...prev,
        {modelNumber: '', quantity: '0', cost: ''}
      ]
    })
  }

  const handleAddReturnRowClick = () => {
    setReturnItems((prev) => {
      console.log(prev);
      return [
        ...prev,
        { modelNumber: "", quantity: "", returnType: "", ticketNumber: "", isOpen: false },
      ];
    });
  };

  const handleSaveReturnsClick = () => {
    console.log(returnItems);
    if (ret) {
      dispatch(updateRet(Date.parse(selectedDate),{
        items: returnItems,
      }, openSnackbar))
    } else {
      dispatch(createRet({
        items: returnItems,
        returnDate: Date.parse(selectedDate)
      }, openSnackbar))
    }
  };


  const handleAddRowClick = () => {
    setItems((prev) => {
      return [
        ...prev,
        { quantity: "0", cost: "$0.00", modelNumber: "" },
      ];
    });
  };

  const handleRemoveRowClick = (selectedRowIds) => {
    setItems((prev) => {
      return [...prev.filter((product, idx) => !selectedRowIds.includes(idx))];
    });
  };

  const dataLoading =
    ((invoicesLoading ?? false) && invoices.length === 0) ||
    ((whitesLoading ?? false) && whites.length === 0);

  if (isLoading || dataLoading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          paddingTop: "20px",
        }}
      >
        <Spinner />
      </div>
    );
  }

  return (
    <Container
      className={styles.orderSheet}
      style={{
        width: "100%",
        paddingTop: "70px",
        paddingBottom: "200px",
        minHeight: '100vh',
        border: "none",
        alignItems: "center",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div className={styles.controls}>
      
        <div className="col-md-6 col-sm-12" style={{textAlign: 'center'}}>
          <h1>Order Sheet</h1>
          <div className={styles.buttons}>
            <button
              // disabled={status === 'Paid' ? true : false}
              className={styles.btn}
              onClick={() => handleAddRowClick()}
            >
              Add Row
            </button>
            <div className={styles.toolBar}>
              <Container disableGutters>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <KeyboardDatePicker
                    margin="normal"
                    id="date-picker-dialog"
                    label="Filter By Received By Date"
                    format="MM/dd/yyyy"
                    value={selectedDate}
                    onChange={handleDateChange}
                    KeyboardButtonProps={{
                      "aria-label": "change date",
                    }}
                  />
                </MuiPickersUtilsProvider>
              </Container>
            </div>
          </div>
        </div>
        <div className="col-md-6 col-sm-12">
            <div className={styles.buttons}>
              <div>
                <ProgressButton
                  onClick={(e) => sendOrderSheet(e, profiles.emailLawList, "L")}
                  state={sendLawStatus}
                  onSuccess={() =>
                    openSnackbar("Order Sheet sent successfully")
                  }
                >
                  Send to LAW
                </ProgressButton>
              </div>
              <div className={styles.saveButton}>
                <ProgressButton
                  onClick={(e) =>
                    sendOrderSheet(e, profiles.emailWarehouseList, "W")
                  }
                  state={sendStatus}
                  onSuccess={() =>
                    openSnackbar("Order Sheet sent successfully")
                  }
                >
                  Send to Warehouse
                </ProgressButton>
              </div>
            </div>
          </div>
      </div>

      {/* <span>
        Products are displayed from invoices:{" "}
        {filteredInvoices.map((i) => {
          return `${i.invoiceNumber}, `;
        })}
      </span> */}
      
       {
        !matches ?
            <FittedOrderGrid
              rows={mobileRows}
              columns={mobileCols}
              onCellsChanged={handleChanges}
              enableRowSelection
            />
            :
            <FittedOrderGrid
              rows={rows}
              columns={cols}
              onCellsChanged={handleChanges}
              enableRowSelection
            />
       }
      <div className={styles.sheetSection}>
      <h2>Returns</h2>
        <div className="row">
          <div className="col-md-6 col-sm-12">
            <div className={styles.buttons}>
              <button
                // disabled={status === 'Paid' ? true : false}
                className={styles.btn}
                onClick={() => handleAddReturnRowClick()}
              >
                Add Row
              </button>
              <button
                // disabled={status === 'Paid' ? true : false}
                className={styles.btn}
                onClick={() => handleSaveReturnsClick()}
                disabled={returnItems.length > 0 ? false : true}
              >
                Save Returns
              </button>
            </div>
          </div>
        </div>
        {
          !matches ?
              <FittedOrderGrid
                rows={returnRows}
                columns={returnMobileCols}
                onCellsChanged={handleReturnChanges}
                enableRowSelection
              />
              :
              <FittedOrderGrid
              rows={returnRows}
              columns={returnCols}
              onCellsChanged={handleReturnChanges}
              enableRowSelection
          />
        }
      
      </div>
      <div className={styles.sheetSection}>
        <h2>Inventory</h2>
        <div className="row">
          <div className="col-md-6 col-sm-12">
            <div className={styles.buttons}>
              <button
                // disabled={status === 'Paid' ? true : false}
                className={styles.btn}
                onClick={() => handleAddDNORowClick()}
              >
                Add Row
              </button>
            </div>
          </div>
        </div>

          <FittedOrderGrid
            rows={dnoRows}
            columns={matches ? dnoCols : mobileCols}
            onCellsChanged={handleDNOChanges}
            enableRowSelection
          />
      
      </div>
      <div className={styles.sheetSection}>
        <h2>Whites</h2>
        {
        !matches ?
            <FittedOrderGrid
              rows={whieMobileRows}
              columns={mobileCols}
              onCellsChanged={handleChanges}
              enableRowSelection
            />
            :
            <FittedOrderGrid
              rows={whiteRows}
              columns={cols}
              onCellsChanged={handleChanges}
              enableRowSelection
            />
       }
      </div>
      <div className={styles.priceDifferenceSection}>
        <h2>Price Differences</h2>
        {priceDifferenceGroups.length === 0 ? (
          <p>No same-model cost differences for {selectedDate}.</p>
        ) : (
          <div className={styles.priceDifferenceList}>
            {priceDifferenceGroups.map((modelGroup) => (
              <div className={styles.priceDifferenceCard} key={modelGroup.modelNumber}>
                <div className={styles.priceDifferenceModel}>{modelGroup.modelNumber}</div>
                <div className={styles.priceDifferenceRows}>
                  {modelGroup.priceGroups.map((priceGroup) => (
                    <div
                      className={styles.priceDifferenceRow}
                      key={`${modelGroup.modelNumber}-${priceGroup.unitPriceCents}-${priceGroup.unitCostCents}`}
                    >
                      <div className={styles.priceDifferenceSummary}>
                        <span>Price: {dollar_formatter.format(formatCentsToDollars(priceGroup.unitPriceCents))}</span>
                        <span>Cost: {dollar_formatter.format(formatCentsToDollars(priceGroup.unitCostCents))}</span>
                        <span>Qty: {priceGroup.quantity}</span>
                        {priceGroup.sourceTypes.map((sourceType) => (
                          <span key={sourceType}>{sourceType}</span>
                        ))}
                      </div>
                      <div className={styles.priceDifferenceInvoices}>
                        {priceGroup.invoices.map((invoice) => (
                          <button
                            className={styles.invoiceLinkButton}
                            key={invoice.id}
                            type="button"
                            onClick={() => history.push(`/invoice/${invoice.id}`)}
                          >
                            {invoice.customer || "Open invoice"}
                            {invoice.receivedByDate ? ` (${moment(invoice.receivedByDate).format("M/D/YY")})` : ""}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className={styles.sendHistorySection}>
        <h2>Order Sheet Send History</h2>
        {historyLoading ? (
          <p>Loading history...</p>
        ) : sendHistory.length === 0 ? (
          <p>No send history for {selectedDate}.</p>
        ) : (
          <div className={styles.sendHistoryList}>
            {sendHistory.map((entry) => (
              <div className={styles.sendHistoryRow} key={entry._id}>
                <div className={styles.sendHistoryType}>
                  {entry.flag === 'L' ? 'Sent to LAW' : 'Sent to Warehouse'}
                </div>
                <div className={styles.sendHistoryMeta}>
                  {(entry.sentByName || entry.sentByEmail || 'Unknown user')} • {entry.sentAt ? moment(entry.sentAt).format("MMM D, YYYY h:mm A") : "—"}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
    </Container>
  );
};

export default OrderSheet;
